import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Edit2, Trash2, Search, FlaskRound, X, Tag, Home, Clock, Droplets, Upload } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LabTestsModule = ({ user }) => {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({
    test_code: '', name: '', category: '', mrp: '', offer_price: '',
    sample_requirement: '', tat: '', home_collection: true, preparation: '',
    active: true, description: '', b2b_cost: '', internal_note: '',
  });

  const fetchTests = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (filterCategory) params.set('category', filterCategory);
      const { data } = await axios.get(`${API}/admin/lab-tests?${params}`, { withCredentials: true });
      setTests(data);
    } catch { toast.error('Failed to load tests'); }
    setLoading(false);
  }, [search, filterCategory]);

  useEffect(() => { fetchTests(); }, [fetchTests]);

  const categories = [...new Set(tests.map(t => t.category).filter(Boolean))].sort();

  const resetForm = () => {
    setForm({ test_code: '', name: '', category: '', mrp: '', offer_price: '', sample_requirement: '', tat: '', home_collection: true, preparation: '', active: true, description: '', b2b_cost: '', internal_note: '' });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (!payload.offer_price) payload.offer_price = null;
      if (!payload.b2b_cost) payload.b2b_cost = null;
      if (editingId) {
        await axios.put(`${API}/admin/lab-tests/${editingId}`, payload, { withCredentials: true });
        toast.success('Test updated');
      } else {
        if (!payload.test_code) payload.test_code = `LT${Date.now()}`;
        await axios.post(`${API}/admin/lab-tests`, payload, { withCredentials: true });
        toast.success('Test added');
      }
      resetForm();
      fetchTests();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const startEdit = (t) => {
    setEditingId(t.test_code);
    setForm({
      test_code: t.test_code || '',
      name: t.name || '',
      category: t.category || '',
      mrp: t.mrp || '',
      offer_price: t.offer_price || '',
      sample_requirement: t.sample_requirement || '',
      tat: t.tat || '',
      home_collection: t.home_collection !== false,
      preparation: t.preparation || '',
      active: t.active !== false,
      description: t.description || '',
      b2b_cost: t.b2b_cost || '',
      internal_note: t.internal_note || '',
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Delete "${t.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/lab-tests/${t.test_code}`, { withCredentials: true });
      toast.success('Test deleted');
      fetchTests();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const quickToggle = async (t, field) => {
    try {
      await axios.put(`${API}/admin/lab-tests/${t.test_code}`, { [field]: !t[field] }, { withCredentials: true });
      toast.success(`${field === 'active' ? (t.active ? 'Deactivated' : 'Activated') : (t.home_collection ? 'Disabled' : 'Enabled')} home collection`);
      fetchTests();
    } catch { toast.error('Failed'); }
  };

  const testsWithOffer = tests.filter(t => t.offer_price);
  const testsActive = tests.filter(t => t.active);

  const handleBulkCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBulkUploading(true);
    setBulkResult(null);
    try {
      const text = await file.text();
      const lines = text.split('\n').filter(l => l.trim());
      if (lines.length < 2) { toast.error('CSV must have header + data rows'); setBulkUploading(false); return; }
      const header = lines[0].replace(/^\uFEFF/, '').split(',').map(h => h.trim().toLowerCase());
      const codeIdx = header.findIndex(h => h === 'test_code');
      const offerIdx = header.findIndex(h => h === 'offer_price');
      const mrpIdx = header.findIndex(h => h === 'mrp');
      if (codeIdx === -1 || offerIdx === -1) {
        toast.error('CSV must have "test_code" and "offer_price" columns');
        setBulkUploading(false);
        return;
      }
      const items = [];
      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(',').map(c => c.trim().replace(/^"|"$/g, ''));
        if (!cols[codeIdx]) continue;
        const item = { test_code: cols[codeIdx], offer_price: cols[offerIdx] || '' };
        if (mrpIdx !== -1 && cols[mrpIdx]) item.mrp = cols[mrpIdx];
        items.push(item);
      }
      if (items.length === 0) { toast.error('No valid rows found'); setBulkUploading(false); return; }
      const { data } = await axios.post(`${API}/admin/lab-tests/bulk-offer`, { items }, { withCredentials: true });
      setBulkResult(data);
      toast.success(`Updated ${data.updated} tests`);
      fetchTests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
    setBulkUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const downloadTemplate = () => {
    const header = 'test_code,offer_price\n';
    const rows = tests.slice(0, 5).map(t => `${t.test_code},${t.offer_price || ''}`).join('\n');
    const sample = `# Example: set offer_price, leave blank or "clear" to remove offer\n${header}${rows}\nLT001,250\nLT002,clear\n`;
    const blob = new Blob([sample], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'offer_price_template.csv';
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  };

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-xl p-3 shadow-sm text-center">
          <p className="text-2xl font-bold text-[#0A2540]">{tests.length}</p>
          <p className="text-xs text-[#94A3B8]">Total Tests</p>
        </div>
        <div className="bg-white rounded-xl p-3 shadow-sm text-center">
          <p className="text-2xl font-bold text-green-600">{testsWithOffer.length}</p>
          <p className="text-xs text-[#94A3B8]">With Offer Price</p>
        </div>
        <div className="bg-white rounded-xl p-3 shadow-sm text-center">
          <p className="text-2xl font-bold text-sky-600">{testsActive.length}</p>
          <p className="text-xs text-[#94A3B8]">Active</p>
        </div>
      </div>

      {/* Search + Filter + Add */}
      <div className="flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, code..." className="pl-10 h-11 rounded-xl" data-testid="lab-test-search" />
        </div>
        <select className="h-11 border rounded-xl px-3 text-sm" value={filterCategory} onChange={e => setFilterCategory(e.target.value)} data-testid="lab-test-filter-category">
          <option value="">All Categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <Button onClick={() => { resetForm(); setShowForm(!showForm); }} className="bg-[#0A2540] rounded-xl h-11 px-4" data-testid="add-lab-test-btn">
          <Plus className="h-4 w-4 mr-1" /> Add Test
        </Button>
        <Button onClick={() => setShowBulkUpload(!showBulkUpload)} variant="outline" className="rounded-xl h-11 px-4 border-green-300 text-green-700 hover:bg-green-50" data-testid="bulk-offer-btn">
          <Upload className="h-4 w-4 mr-1" /> Bulk Offer
        </Button>
      </div>

      {/* Bulk Offer Upload */}
      {showBulkUpload && (
        <div className="bg-green-50 border border-green-200 rounded-2xl p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-green-800 text-sm">Bulk Offer Price Upload</h3>
            <button onClick={() => { setShowBulkUpload(false); setBulkResult(null); }}><X className="h-4 w-4 text-green-600" /></button>
          </div>
          <p className="text-xs text-green-700">Upload a CSV with columns: <code className="bg-green-100 px-1 rounded">test_code</code>, <code className="bg-green-100 px-1 rounded">offer_price</code>. Optionally include <code className="bg-green-100 px-1 rounded">mrp</code> to update MRP too. Set offer_price to blank or &quot;clear&quot; to remove an offer.</p>
          <div className="flex gap-2 items-center">
            <input ref={fileInputRef} type="file" accept=".csv" onChange={handleBulkCSV} className="text-sm file:mr-2 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-green-600 file:text-white hover:file:bg-green-700" data-testid="bulk-csv-input" />
            <Button type="button" onClick={downloadTemplate} variant="outline" className="rounded-xl h-9 px-3 text-xs border-green-300" data-testid="download-template-btn">
              Download Template
            </Button>
          </div>
          {bulkUploading && <p className="text-sm text-green-600 animate-pulse">Uploading...</p>}
          {bulkResult && (
            <div className="bg-white rounded-xl p-3 text-sm space-y-1" data-testid="bulk-result">
              <p className="font-bold text-green-800">Updated: {bulkResult.updated} / {bulkResult.total}</p>
              {bulkResult.skipped?.length > 0 && (
                <div>
                  <p className="text-red-600 font-medium text-xs">Skipped ({bulkResult.skipped.length}):</p>
                  {bulkResult.skipped.map((s, i) => (
                    <p key={i} className="text-xs text-red-500">{s.test_code}: {s.reason}</p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-5 shadow-sm space-y-4 border border-[#E2E8F0]">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-[#0A2540]">{editingId ? `Edit: ${editingId}` : 'Add New Test'}</h3>
            <button type="button" onClick={resetForm}><X className="h-4 w-4 text-[#94A3B8]" /></button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {!editingId && (
              <div><Label className="text-xs">Test Code *</Label><Input value={form.test_code} onChange={e => setForm(p => ({ ...p, test_code: e.target.value }))} placeholder="e.g. LT098" className="h-10 rounded-xl mt-0.5 text-sm" data-testid="lab-test-code" /></div>
            )}
            <div><Label className="text-xs">Test Name *</Label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required placeholder="e.g. Complete Blood Count" className="h-10 rounded-xl mt-0.5 text-sm" data-testid="lab-test-name" /></div>
            <div><Label className="text-xs">Category *</Label>
              <Input list="cat-list" value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} required placeholder="e.g. Hematology" className="h-10 rounded-xl mt-0.5 text-sm" data-testid="lab-test-category" />
              <datalist id="cat-list">{categories.map(c => <option key={c} value={c} />)}</datalist>
            </div>
          </div>

          {/* Pricing Section */}
          <div className="bg-[#F8FAFC] rounded-xl p-4 space-y-3">
            <p className="text-xs font-bold text-[#0A2540] uppercase tracking-wider flex items-center gap-1"><Tag className="h-3 w-3" /> Pricing</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div><Label className="text-xs">MRP (Rs.) *</Label><Input type="number" min="0" step="0.01" value={form.mrp} onChange={e => setForm(p => ({ ...p, mrp: e.target.value }))} required placeholder="300" className="h-10 rounded-xl mt-0.5 text-sm" data-testid="lab-test-mrp" /></div>
              <div>
                <Label className="text-xs text-green-700">Offer Price (Rs.)</Label>
                <Input type="number" min="0" step="0.01" value={form.offer_price} onChange={e => setForm(p => ({ ...p, offer_price: e.target.value }))} placeholder="Leave blank = no offer" className="h-10 rounded-xl mt-0.5 text-sm border-green-200 focus:border-green-400" data-testid="lab-test-offer-price" />
                {form.offer_price && form.mrp && Number(form.offer_price) < Number(form.mrp) && (
                  <p className="text-[10px] text-green-600 mt-0.5">Discount: Rs.{(Number(form.mrp) - Number(form.offer_price)).toFixed(0)} ({((1 - Number(form.offer_price) / Number(form.mrp)) * 100).toFixed(0)}% off)</p>
                )}
              </div>
              <div><Label className="text-xs text-[#94A3B8]">B2B Cost (internal)</Label><Input type="number" min="0" step="0.01" value={form.b2b_cost} onChange={e => setForm(p => ({ ...p, b2b_cost: e.target.value }))} placeholder="Internal cost" className="h-10 rounded-xl mt-0.5 text-sm" /></div>
            </div>
          </div>

          {/* Clinical Details */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div><Label className="text-xs">Sample Requirement</Label><Input value={form.sample_requirement} onChange={e => setForm(p => ({ ...p, sample_requirement: e.target.value }))} placeholder="e.g. WB-EDTA" className="h-10 rounded-xl mt-0.5 text-sm" data-testid="lab-test-sample" /></div>
            <div><Label className="text-xs">TAT (Turnaround Time)</Label><Input value={form.tat} onChange={e => setForm(p => ({ ...p, tat: e.target.value }))} placeholder="e.g. 4 hrs" className="h-10 rounded-xl mt-0.5 text-sm" data-testid="lab-test-tat" /></div>
            <div><Label className="text-xs">Preparation</Label><Input value={form.preparation} onChange={e => setForm(p => ({ ...p, preparation: e.target.value }))} placeholder="e.g. Fasting 8-10 hrs" className="h-10 rounded-xl mt-0.5 text-sm" data-testid="lab-test-preparation" /></div>
          </div>

          {/* Toggles */}
          <div className="flex gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.active} onChange={e => setForm(p => ({ ...p, active: e.target.checked }))} className="w-4 h-4 rounded" data-testid="lab-test-active" />
              <span className="text-sm">Active</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.home_collection} onChange={e => setForm(p => ({ ...p, home_collection: e.target.checked }))} className="w-4 h-4 rounded" data-testid="lab-test-home-collection" />
              <span className="text-sm">Home Collection</span>
            </label>
          </div>

          {/* Description + Internal Note */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label className="text-xs">Description</Label><Input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Public description" className="h-10 rounded-xl mt-0.5 text-sm" /></div>
            <div><Label className="text-xs text-[#94A3B8]">Internal Note</Label><Input value={form.internal_note} onChange={e => setForm(p => ({ ...p, internal_note: e.target.value }))} placeholder="Admin-only note" className="h-10 rounded-xl mt-0.5 text-sm" /></div>
          </div>

          <Button type="submit" className="w-full bg-green-600 hover:bg-green-700 rounded-xl h-11 text-sm font-bold" data-testid="lab-test-submit">
            {editingId ? 'Update Test' : 'Add Test'}
          </Button>
        </form>
      )}

      {/* Test List */}
      <p className="text-xs text-[#94A3B8]">{tests.length} tests {filterCategory && `in ${filterCategory}`}</p>

      {loading ? <p className="text-sm text-[#94A3B8]">Loading...</p> : tests.length === 0 ? <p className="text-sm text-[#94A3B8]">No tests found</p> : (
        <div className="space-y-2">
          {tests.map(t => (
            <div key={t.test_code} className={`bg-white rounded-xl p-3.5 shadow-sm hover:shadow-md transition-shadow ${!t.active ? 'opacity-50' : ''}`} data-testid={`lab-test-${t.test_code}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="p-2 rounded-lg bg-sky-50 flex-shrink-0"><FlaskRound className="h-4 w-4 text-sky-600" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-bold text-[#0A2540] text-sm truncate">{t.name}</p>
                      <span className="text-[10px] text-[#94A3B8] bg-[#F1F5F9] px-1.5 py-0.5 rounded font-mono">{t.test_code}</span>
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-[#64748B]">
                      <span>{t.category}</span>
                      {t.sample_requirement && <span className="flex items-center gap-0.5"><Droplets className="h-2.5 w-2.5" />{t.sample_requirement}</span>}
                      {t.tat && <span className="flex items-center gap-0.5"><Clock className="h-2.5 w-2.5" />{t.tat}</span>}
                      {t.home_collection && <span className="text-green-600 flex items-center gap-0.5"><Home className="h-2.5 w-2.5" />Home</span>}
                      {!t.active && <span className="text-red-500 font-bold">INACTIVE</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                  <div className="text-right">
                    {t.offer_price ? (
                      <>
                        <span className="font-bold text-green-600 text-sm">Rs.{t.offer_price}</span>
                        <span className="text-xs text-[#94A3B8] line-through ml-1">Rs.{t.mrp}</span>
                      </>
                    ) : (
                      <span className="font-bold text-[#0A2540] text-sm">Rs.{t.mrp}</span>
                    )}
                  </div>
                  <button onClick={() => startEdit(t)} className="p-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100" data-testid={`edit-lab-test-${t.test_code}`}>
                    <Edit2 className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => handleDelete(t)} className="p-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100" data-testid={`delete-lab-test-${t.test_code}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LabTestsModule;
